import { useState, useRef, useCallback, useEffect, RefObject } from "react";
import { createWorker, Worker, PSM } from "tesseract.js";
import useCameraBarcode from "../hooks/useCameraBarcode";
import { usePageTitle } from "../hooks/usePageTitle";

// Test page: single-capture OCR that dumps ALL text Tesseract can make out.
// Unlike the production TNT scanner (useTntOcr), this does NOT loop, does NOT
// whitelist digits, and does NOT filter to the 88XXXXXXXXXXX pattern — it's a
// scratch tool for seeing exactly what the OCR reads off a label.

type CropMode = "full" | "centre";

// Mirror of the production crop in useTntOcr.preprocessFrame, so "centre"
// mode reflects what the real scanner sees. "full" reads the whole frame.
const drawFrame = (video: HTMLVideoElement, mode: CropMode): HTMLCanvasElement | null => {
    if (video.readyState < 2) return null;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;

    let cropW = vw, cropH = vh, cropX = 0, cropY = 0;
    if (mode === "centre") {
        cropW = Math.floor(vw * 0.80);
        cropH = Math.floor(vh * 0.15);
        cropX = Math.floor((vw - cropW) / 2);
        cropY = Math.floor((vh - cropH) / 2);
    }

    const canvas = document.createElement("canvas");
    canvas.width = cropW;
    canvas.height = cropH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
    return canvas;
};

const TntOcrTest = () => {
    usePageTitle("TNT OCR Test");

    const [cropMode, setCropMode] = useState<CropMode>("full");
    const [busy, setBusy] = useState(false);
    const [ocrText, setOcrText] = useState("");
    const [elapsedMs, setElapsedMs] = useState<number | null>(null);

    const workerRef = useRef<Worker | null>(null);

    const { isCameraActive, isCameraAvailable, cameraError, toggleCamera, videoRef } =
        useCameraBarcode({
            // No onScan work — we only want the live video stream + torch.
            onScan: () => { },
            // Pause the built-in barcode detection; this page captures manually.
            enabled: false,
            torch: false,
        });

    // Spin up a single OCR worker for the page lifetime. No char whitelist and
    // SPARSE_TEXT so it returns every word it can find, anywhere in the frame.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const worker = await createWorker("eng");
            if (cancelled) { await worker.terminate(); return; }
            await worker.setParameters({ tessedit_pageseg_mode: PSM.SPARSE_TEXT });
            workerRef.current = worker;
        })();
        return () => {
            cancelled = true;
            if (workerRef.current) {
                workerRef.current.terminate().catch(() => { });
                workerRef.current = null;
            }
        };
    }, []);

    const capture = useCallback(async () => {
        const video = videoRef.current;
        const worker = workerRef.current;
        if (!video || !worker || busy) return;

        const canvas = drawFrame(video, cropMode);
        if (!canvas) {
            setOcrText("(no frame — is the camera on and focused?)");
            return;
        }

        setBusy(true);
        setOcrText("");
        setElapsedMs(null);
        try {
            const t0 = performance.now();
            const result = await worker.recognize(canvas);
            setElapsedMs(Math.round(performance.now() - t0));
            const text = result.data.text.trim();
            setOcrText(text || "(no text detected)");
            console.log("[TNT OCR TEST] full result:", result.data.text);
        } catch (err) {
            console.warn("[TNT OCR TEST] recognize error:", err);
            setOcrText("(OCR error — see console)");
        } finally {
            setBusy(false);
        }
    }, [videoRef, cropMode, busy]);

    return (
        <div style={{ padding: 16, maxWidth: 720, margin: "0 auto" }}>
            <h1 style={{ marginTop: 0 }}>TNT OCR Test</h1>
            <p style={{ color: "#666", marginTop: 0 }}>
                Single-capture OCR scratch page. Turn the camera on, aim at a label,
                press <strong>Capture</strong>, and the raw OCR text appears below.
            </p>

            {!isCameraAvailable && (
                <p style={{ color: "#b00" }}>No camera available on this device.</p>
            )}
            {cameraError && (
                <p style={{ color: "#b00" }}>Camera error: {cameraError}</p>
            )}

            <div style={{ position: "relative", background: "#000", borderRadius: 8, overflow: "hidden" }}>
                <video
                    ref={videoRef as RefObject<HTMLVideoElement>}
                    autoPlay
                    playsInline
                    muted
                    style={{ width: "100%", display: "block", maxHeight: "50vh", objectFit: "contain" }}
                />
                {cropMode === "centre" && isCameraActive && (
                    // Visualise the production centre strip (80% x 15%).
                    <div
                        style={{
                            position: "absolute",
                            left: "10%", right: "10%",
                            top: "42.5%", height: "15%",
                            border: "2px solid orange",
                            pointerEvents: "none",
                        }}
                    />
                )}
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                <button onClick={toggleCamera} disabled={!isCameraAvailable}>
                    {isCameraActive ? "Stop camera" : "Start camera"}
                </button>
                <button onClick={capture} disabled={!isCameraActive || busy}>
                    {busy ? "Reading…" : "Capture"}
                </button>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span>Region:</span>
                    <select
                        value={cropMode}
                        onChange={(e) => setCropMode(e.target.value as CropMode)}
                    >
                        <option value="full">Full frame</option>
                        <option value="centre">Centre strip (production crop)</option>
                    </select>
                </label>
            </div>

            <div style={{ marginTop: 16 }}>
                <div style={{ color: "#666", fontSize: 13 }}>
                    OCR output{elapsedMs != null ? ` (${elapsedMs}ms)` : ""}:
                </div>
                <pre
                    style={{
                        background: "#f5f5f5",
                        border: "1px solid #ddd",
                        borderRadius: 6,
                        padding: 12,
                        minHeight: 80,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        marginTop: 4,
                    }}
                >
                    {ocrText}
                </pre>
            </div>
        </div>
    );
};

export default TntOcrTest;
